/**
 * 가이드의 매뉴얼 묶음 둘 — 매뉴얼마다 잠김 여부를 붙인다. 빈 묶음은 세우지 않는다.
 * - 화면별(`toGuideHomeGroups`): 앱 사이드바 묶음 차례 — 홈 「화면별로 찾아보기」.
 * - 목적별(`toGuidePurposeGroups`): 「무엇을 하고 싶으세요?」 카드 차례 — 왼쪽 길잡이.
 */

import type { ManualId, ManualEntry, ManualPurpose, ManualMenuGroup } from 'src/types/manual/manual-domain';

export type GuideHomeItem = { entry: ManualEntry; locked: boolean };

export type GuideHomeGroup = { group: ManualMenuGroup; label: string; items: GuideHomeItem[] };

/**
 * 길잡이 묶음 하나 — 목적 카드 하나, 또는 어느 목적에도 안 든 매뉴얼을 모은 맨 끝 묶음(`key` = `GUIDE_NAV_REST_KEY`).
 * `label` 은 목적의 짧은 이름(`shortTitle`) · `icon` 은 목적의 색 아이콘(`fluent-emoji:<이름>`).
 */
export type GuideNavGroup = { key: string; emoji: string; icon: string; label: string; items: GuideHomeItem[] };

/** 어느 목적에도 안 든 매뉴얼 묶음의 열쇠 */
export const GUIDE_NAV_REST_KEY = 'rest';

/**
 * @param catalog 매뉴얼 목록(차례가 묶음 안의 차례)
 * @param groups 묶음 차례와 머리 글자
 * @param canRead 매뉴얼 하나를 읽을 수 있나 — 못 읽으면 `locked`
 */
export function toGuideHomeGroups(
  catalog: readonly ManualEntry[],
  groups: readonly { group: ManualMenuGroup; label: string }[],
  canRead: (id: ManualId) => boolean
): GuideHomeGroup[] {
  return groups
    .map(({ group, label }) => ({
      group,
      label,
      items: catalog
        .filter((entry) => entry.menuGroup === group)
        .map((entry) => ({ entry, locked: !canRead(entry.id) })),
    }))
    .filter((g) => g.items.length > 0);
}

/**
 * 목적 카드 차례로 매뉴얼을 묶는다 — 묶음 안 차례는 카드의 `manuals` 차례다(한 매뉴얼이 두 목적에 들면 두 곳에 선다).
 * 어느 목적에도 안 든 매뉴얼은 맨 끝 묶음 하나에 모은다 — `restLead` 를 그 차례로 먼저, 나머지는 목록 차례로.
 * 길잡이에서 빠지는 매뉴얼이 없게.
 * @param catalog 매뉴얼 목록(맨 끝 묶음의 차례)
 * @param purposes 목적 카드(묶음 차례 · 머리 글자 = 짧은 이름)
 * @param rest 맨 끝 묶음의 머리 글자 · 그림
 * @param canRead 매뉴얼 하나를 읽을 수 있나 — 못 읽으면 `locked`
 * @param restLead 맨 끝 묶음 맨 위에 세울 매뉴얼(목적에 든 것은 무시한다)
 */
export function toGuidePurposeGroups(
  catalog: readonly ManualEntry[],
  purposes: readonly ManualPurpose[],
  rest: { emoji: string; icon: string; label: string },
  canRead: (id: ManualId) => boolean,
  restLead: readonly ManualId[] = []
): GuideNavGroup[] {
  const byId = new Map(catalog.map((entry) => [entry.id, entry] as const));
  const item = (entry: ManualEntry): GuideHomeItem => ({ entry, locked: !canRead(entry.id) });
  const covered = new Set(purposes.flatMap((purpose) => purpose.manuals));
  const groups: GuideNavGroup[] = purposes.map((purpose) => ({
    key: purpose.key,
    emoji: purpose.emoji,
    icon: purpose.icon,
    label: purpose.shortTitle,
    items: purpose.manuals.flatMap((id) => {
      const entry = byId.get(id);
      return entry ? [item(entry)] : [];
    }),
  }));
  const uncovered = catalog.filter((entry) => !covered.has(entry.id));
  const lead = (entry: ManualEntry) => {
    const index = restLead.indexOf(entry.id);
    return index < 0 ? restLead.length : index;
  };
  const restItems = uncovered.map((entry, index) => ({ entry, index })).sort((a, b) => lead(a.entry) - lead(b.entry) || a.index - b.index);
  groups.push({ key: GUIDE_NAV_REST_KEY, ...rest, items: restItems.map(({ entry }) => item(entry)) });
  return groups.filter((group) => group.items.length > 0);
}
